import { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAppStore } from './store/useAppStore';
import { supabase } from './utils/supabaseStorage';
import { hyperSessionFromSupabaseUser } from './types/session';
import { ModalContainer } from './components/ModalContainer';
import { RequireAuth } from './components/RequireAuth';
import { RequireStaff } from './components/RequireStaff';
import { RequireCreator } from './components/RequireCreator';
import { StudioLayout } from './components/studio/StudioLayout';
import { StudioHomePage } from './pages/studio/StudioHomePage';
import { StudioAgentEditPage } from './pages/studio/StudioAgentEditPage';
import { StudioAgentVersionsPage } from './pages/studio/StudioAgentVersionsPage';
import { StudioProfilePage } from './pages/studio/StudioProfilePage';
import { StudioGuidelinesPage } from './pages/studio/StudioGuidelinesPage';
import { CreatorPublicProfilePage } from './pages/public/CreatorPublicProfilePage';
import { CreatorPublicLandingPage } from './pages/public/CreatorPublicLandingPage';
import { AppTOSPage } from './pages/AppTOSPage';
import { ProPage } from './pages/ProPage';
import { AdminPage } from './pages/AdminPage';
import PublicModelPage from './pages/PublicModelPage';
import { PublicProPage } from './pages/PublicProPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { ProcessingMonitorPage } from './pages/ProcessingMonitorPage';
import { UnleashPage } from './pages/UnleashPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { PublicProMaxPage } from './pages/PublicProMaxPage';
import { ProQuestPage } from './pages/ProQuestPage';
import { HomePage } from './pages/HomePage';
import { AboutHyperAppStorePage } from './pages/AboutHyperAppStorePage';
import { LibraryPage } from './pages/LibraryPage';
import { ProfilePage } from './pages/ProfilePage';
import { StorefrontLayout } from './components/store/StorefrontLayout';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { UpdatePasswordPage } from './pages/UpdatePasswordPage';
import { StorefrontAgentPage } from './pages/StorefrontAgentPage';
import { MainContent } from './components/MainContent';
import { SiteAmbientBackground } from './components/SiteAmbientBackground';

const DirectProFormRoute = () => {
  const pathname = window.location.pathname;
  window.location.href = `/pro${pathname}${window.location.search}`;
  return null;
};

function App() {
  const setSession = useAppStore((s) => s.setSession);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const sync = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          await setSession(hyperSessionFromSupabaseUser(session.user));
        } else {
          await setSession(null);
        }
      } catch (e) {
        console.error('[App] auth sync failed', e);
        try {
          await setSession(null);
        } catch {
          /* ignore */
        }
      } finally {
        setAuthReady(true);
      }
    };
    void sync();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      try {
        if (session?.user) {
          await setSession(hyperSessionFromSupabaseUser(session.user));
        } else {
          await setSession(null);
        }
      } catch (e) {
        console.error('[App] onAuthStateChange setSession failed', e);
      }
    });
    return () => subscription.unsubscribe();
  }, [setSession]);

  if (!authReady) {
    return (
      <div className="relative min-h-dvh w-full max-w-[100vw] overflow-x-hidden bg-transparent">
        <SiteAmbientBackground />
        <div className="relative z-10 flex min-h-dvh w-full min-w-0 items-center justify-center text-th-secondary text-sm">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-dvh w-full max-w-[100vw] overflow-x-hidden bg-transparent">
      <SiteAmbientBackground />
      <div className="relative z-10 min-h-dvh w-full min-w-0 bg-transparent">
        <Routes>
        <Route element={<StorefrontLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutHyperAppStorePage />} />
          <Route
            path="/library"
            element={
              <RequireAuth>
                <LibraryPage />
              </RequireAuth>
            }
          />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/update-password" element={<UpdatePasswordPage />} />
        <Route path="/appTOS" element={<AppTOSPage />} />
        <Route path="/admin/*" element={<AdminPage />} />

        {/* Public creator landing — anon-friendly via get_creator_public_profile RPC. */}
        <Route path="/c/:handle" element={<CreatorPublicProfilePage />} />
        <Route path="/c/:handle/:slug" element={<CreatorPublicLandingPage />} />

        <Route
          element={
            <RequireCreator>
              <StudioLayout />
            </RequireCreator>
          }
        >
          <Route path="/studio" element={<StudioHomePage />} />
          <Route path="/studio/agents/:templateId" element={<StudioAgentEditPage />} />
          <Route path="/studio/agents/:templateId/versions" element={<StudioAgentVersionsPage />} />
          <Route path="/studio/profile" element={<StudioProfilePage />} />
          <Route path="/studio/help" element={<StudioGuidelinesPage />} />
        </Route>

        <Route
          path="/analytics"
          element={
            <RequireStaff>
              <AnalyticsPage />
            </RequireStaff>
          }
        />
        <Route
          path="/processing-monitor"
          element={
            <RequireStaff>
              <ProcessingMonitorPage />
            </RequireStaff>
          }
        />
        <Route
          path="/proquest"
          element={
            <RequireAuth>
              <RequireStaff>
                <ProQuestPage />
              </RequireStaff>
            </RequireAuth>
          }
        />
        <Route path="/image-request" element={<DirectProFormRoute />} />
        <Route path="/video-request" element={<DirectProFormRoute />} />
        <Route path="/audio-request" element={<DirectProFormRoute />} />
        <Route path="/custom-request" element={<DirectProFormRoute />} />
        <Route path="/*-request" element={<DirectProFormRoute />} />

        <Route
          path="/apps/agent/:templateId"
          element={
            <RequireAuth>
              <StorefrontAgentPage />
            </RequireAuth>
          }
        />

        <Route
          path="/pro"
          element={
            <RequireAuth>
              <RequireStaff>
                <ProPage />
              </RequireStaff>
            </RequireAuth>
          }
        />
        <Route
          path="/pro/:routePath"
          element={
            <RequireAuth>
              <RequireStaff>
                <PublicProPage />
              </RequireStaff>
            </RequireAuth>
          }
        />
        <Route
          path="/p/:routePath"
          element={
            <RequireAuth>
              <PublicModelPage />
            </RequireAuth>
          }
        />
        <Route
          path="/promax/:modelId"
          element={
            <RequireAuth>
              <RequireStaff>
                <PublicProMaxPage />
              </RequireStaff>
            </RequireAuth>
          }
        />
        <Route
          path="/unleash"
          element={
            <RequireAuth>
              <RequireStaff>
                <UnleashPage />
              </RequireStaff>
            </RequireAuth>
          }
        />
        <Route
          path="/projects"
          element={
            <RequireAuth>
              <RequireStaff>
                <ProjectsPage />
              </RequireStaff>
            </RequireAuth>
          }
        />

        <Route
          path="/*"
          element={
            <RequireAuth>
              <RequireStaff>
                <div className="min-h-screen">
                  <MainContent />
                </div>
              </RequireStaff>
            </RequireAuth>
          }
        />
        </Routes>
        <ModalContainer />
      </div>
    </div>
  );
}

export default App;
